import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../../lib/api";
import type { LolerCandidate } from "../../lib/api";
import { RecordPicker } from "../../components/RecordPicker";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { Button } from "../../components/Button";
import { formatDate } from "../../lib/format";

// Session BAD: the LOLER review list. An examination report that arrived by email and was NOT filed on its own, because more than one
// lift could be the subject, none matched, the report states no readable date, or a report for that lift and day is already on
// record. A person names the lift (suggestions first), types the examination date only when the report states none, and confirms.
// Nothing is guessed for them. A report the platform could match to exactly one lift is filed automatically and never appears here.

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-50";

const REASON_TEXT: Record<string, string> = {
  ambiguous_lift: "More than one lift could be the subject",
  no_lift_match: "No lift on the register matches",
  no_examination_date: "The report states no readable examination date",
  examination_date_not_credible: "The examination date is in the future or over three years old",
  duplicate_same_day: "A report for that lift and day is already on record",
};

export function LolerReviewList({ onChanged }: { onChanged?: () => void }) {
  const [items, setItems] = useState<LolerCandidate[] | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    api.loler
      .candidates("open")
      .then((r) => { setItems(r.items); setFailed(false); })
      .catch(() => setFailed(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return <p role="alert" className="mb-4 rounded-ele border border-ele-danger bg-ele-danger-fill px-3 py-2 text-sm text-ele-danger">The LOLER review list could not be read.</p>;
  }
  if (!items || items.length === 0) return null;

  return (
    <section aria-label="LOLER reports to review" id="loler-review" className="mb-6 rounded-ele border border-ele-amber-line bg-ele-surface-soft p-4">
      <h2 className="font-heading text-lg tracking-wide text-ele-strong">LOLER reports to review ({items.length})</h2>
      <p className="mb-3 text-sm text-ele-body">
        These arrived by email and were not filed automatically. Check the PDF, choose the lift, then confirm. Reports that clearly belong to one lift are filed on their own and do not appear here.
      </p>
      <div className="space-y-3">
        {items.map((c) => (
          <ReviewCard key={c.id} c={c} onDone={() => { load(); onChanged?.(); }} />
        ))}
      </div>
    </section>
  );
}

function ReviewCard({ c, onDone }: { c: LolerCandidate; onDone: () => void }) {
  const suggested = useMemo(() => c.suggestions.map((s) => ({ id: s.assetId, label: `${s.ele ?? "Lift"}${s.siteName ? ` (${s.siteName})` : ""}` })), [c.suggestions]);
  const [assetId, setAssetId] = useState(suggested.length === 1 ? suggested[0]!.id : "");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [dupReason, setDupReason] = useState<string | null>(null);

  const needsDate = !c.header.examinationDate || c.reason === "examination_date_not_credible";
  const blocked = !assetId ? "Choose the lift." : needsDate && !date ? "Type the examination date from the report." : null;

  async function confirm(allowDuplicateReason?: string) {
    if (blocked) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.loler.confirmCandidate(c.id, {
        assetId,
        ...(date ? { examinationDate: date } : {}),
        ...(allowDuplicateReason ? { allowDuplicate: true, allowDuplicateReason } : {}),
      });
      if (r.pdfNote) setNote(r.pdfNote);
      onDone();
    } catch (e) {
      if (e instanceof ApiError && e.code === "duplicate_examination") setDupReason("");
      setErr(e instanceof ApiError ? e.message || e.code : "Could not record the report.");
      setBusy(false);
    }
  }

  async function dismiss() {
    setBusy(true);
    setErr(null);
    try {
      await api.loler.dismissCandidate(c.id, reason.trim());
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not dismiss it.");
      setBusy(false);
    }
  }

  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
      <p className="truncate font-heading text-ele-strong">{c.emailSubject || "(no subject)"}</p>
      <p className="text-xs text-ele-body">
        {c.emailFrom || "unknown sender"} · {formatDate(c.emailDate)} · {c.filename}
      </p>
      <p className="mt-1 text-xs text-[var(--ele-amber-text)]">{REASON_TEXT[c.reason] ?? c.reason}. {c.matchNote ?? ""}</p>
      <a href={api.loler.gmailAttachmentHref(c.gmailMessageId, "", c.filename)} target="_blank" rel="noreferrer" className="mt-1 inline-block font-menu text-xs tracking-wide text-ele-blue hover:underline">
        View PDF
      </a>
      <dl className="mt-2 grid gap-x-6 gap-y-0.5 text-xs sm:grid-cols-2">
        <div><dt className="inline text-ele-body">Site: </dt><dd className="inline text-ele-strong">{c.header.siteName ?? c.header.siteAddress ?? "not stated"}</dd></div>
        <div><dt className="inline text-ele-body">Examined: </dt><dd className="inline text-ele-strong">{c.header.examinationDate ? formatDate(c.header.examinationDate) : "not stated"}</dd></div>
        <div><dt className="inline text-ele-body">Examining body: </dt><dd className="inline text-ele-strong">{c.header.inspectorName ?? "not stated"}</dd></div>
        <div><dt className="inline text-ele-body">Serial: </dt><dd className="inline text-ele-strong">{c.header.liftSerialNumber ?? "not stated"}</dd></div>
      </dl>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <div>
          <label className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-body" htmlFor={`rl-lift-${c.id}`}>Lift</label>
          {suggested.length > 0 ? <p className="mb-1 text-xs text-ele-body">The evidence points at: {suggested.map((s) => s.label).join(", ")}</p> : null}
          {suggested.length > 0 ? (
            <select id={`rl-sug-${c.id}`} aria-label="Suggested lift" className={`${inputCls} mb-1`} value={suggested.some((x) => x.id === assetId) ? assetId : ""} onChange={(e) => setAssetId(e.target.value)}>
              <option value="">Choose a suggested lift</option>
              {suggested.map((s) => <option key={`s-${s.id}`} value={s.id}>{s.label} (suggested)</option>)}
            </select>
          ) : null}
          {/* Session SC2B: any other lift is SEARCHED, never chosen from the whole register. */}
          <RecordPicker kind="lift" id={`rl-lift-${c.id}`} value={assetId} onChange={(id) => setAssetId(id)} filters={{ includeRetired: false }} ariaLabel="Search lifts" placeholder="Search lifts by ELE number or site" selectedLabel={suggested.find((x) => x.id === assetId)?.label ?? null} />
        </div>
        {needsDate ? (
          <div>
            <label className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-body" htmlFor={`rl-date-${c.id}`}>Examination date from the report</label>
            <input id={`rl-date-${c.id}`} type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        ) : null}
      </div>

      {err ? <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-danger-fill px-3 py-2 text-xs text-ele-danger">{err}</p> : null}
      {note ? <p className="mt-2 text-xs text-[var(--ele-amber-text)]">{note}</p> : null}

      {dupReason !== null ? (
        <div className="mt-2 rounded-ele border border-ele-amber-line p-2">
          <label className="mb-1 block text-xs text-ele-body" htmlFor={`rl-dup-${c.id}`}>Only if this is a genuine second examination that day, say why</label>
          <input id={`rl-dup-${c.id}`} className={inputCls} value={dupReason} onChange={(e) => setDupReason(e.target.value)} />
          <Button type="button" className="mt-2" disabled={busy || dupReason.trim().length < 5} onClick={() => void confirm(dupReason.trim())}>Record anyway</Button>
        </div>
      ) : null}

      {dismissing ? (
        <div className="mt-2 rounded-ele border border-ele-purple-accent p-2">
          <label className="mb-1 block text-xs text-ele-body" htmlFor={`rl-dis-${c.id}`}>Why is this not being filed?</label>
          <input id={`rl-dis-${c.id}`} className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="mt-2 flex gap-2">
            <Button type="button" disabled={busy || reason.trim().length < 3} onClick={() => void dismiss()}>Dismiss</Button>
            <button type="button" className="font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong" onClick={() => setDismissing(false)}>Cancel</button>
          </div>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button type="button" disabled={busy || blocked != null} onClick={() => void confirm()}>{busy ? "Working" : "Record against this lift"}</Button>
        <button type="button" className="font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong" onClick={() => setDismissing(true)} disabled={busy}>Dismiss</button>
        <button type="button" className="font-menu text-xs tracking-wide text-ele-danger hover:underline" onClick={() => setDeleting(true)} disabled={busy}>Delete</button>
        {blocked ? <span className="text-xs text-ele-body">{blocked}</span> : null}
      </div>

      <ConfirmDeleteModal
        open={deleting}
        title="Delete this review item?"
        message="This removes the prompt only. Nothing statutory is deleted: a report that was recorded from it stays on the lift. The email stays in Gmail."
        confirmLabel="Delete"
        busy={false}
        error={null}
        onConfirm={() => { void api.loler.deleteCandidate(c.id).then(() => { setDeleting(false); onDone(); }).catch(() => setDeleting(false)); }}
        onCancel={() => setDeleting(false)}
      />
    </div>
  );
}
